import { useMutation, useQuery, useQueryClient } from '@tanstack/react-query';
import { apiClient } from '../../../lib/api-client';
import type {
  Company,
  CreateCompanyInput,
  Paginated,
  UpdateCompanyInput,
} from '../../../types/admin';

export function useCompanies(page: number, pageSize: number) {
  return useQuery<Paginated<Company>>({
    queryKey: ['companies', page, pageSize],
    queryFn: () => apiClient.companies.list({ page, pageSize }),
  });
}

function invalidateCompanies(queryClient: ReturnType<typeof useQueryClient>): void {
  void queryClient.invalidateQueries({ queryKey: ['companies'] });
}

/** feat-66: bytes del logo con sesión; la clave incluye el SHA-256 para refrescar al reemplazarlo. */
export function useCompanyLogoBlob(id: string, sha256: string | null) {
  return useQuery<Blob>({
    queryKey: ['company-logo', id, sha256],
    queryFn: () => apiClient.companies.logo(id),
    enabled: Boolean(sha256),
    staleTime: Infinity,
  });
}

export function useUploadCompanyLogo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, archivo }: { id: string; archivo: File }) => apiClient.companies.uploadLogo(id, archivo),
    onSuccess: () => invalidateCompanies(queryClient),
  });
}

export function useRemoveCompanyLogo() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.companies.removeLogo(id),
    onSuccess: () => invalidateCompanies(queryClient),
  });
}

export function useCreateCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (data: CreateCompanyInput) => apiClient.companies.create(data),
    onSuccess: () => invalidateCompanies(queryClient),
  });
}

export function useUpdateCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: ({ id, data }: { id: string; data: UpdateCompanyInput }) =>
      apiClient.companies.update(id, data),
    onSuccess: () => invalidateCompanies(queryClient),
  });
}

export function useDeactivateCompany() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: (id: string) => apiClient.companies.deactivate(id),
    onSuccess: () => invalidateCompanies(queryClient),
  });
}

export function useToggleCompany() {
  const queryClient = useQueryClient();
  return useMutation<unknown, Error, { id: string; activa: boolean }>({
    mutationFn: ({ id, activa }) =>
      activa
        ? apiClient.companies.update(id, { activa: true })
        : apiClient.companies.deactivate(id),
    onSuccess: () => invalidateCompanies(queryClient),
  });
}
